/* ==========================================================================
   Page-Specific Loading Skeletons
   Task F27: Loading skeletons for prescription form, profile form, admin tables
   ========================================================================== */

import { Card } from '@/components/ui/Card'
import {
  Skeleton,
  SkeletonFormField,
  SkeletonTable,
} from '@/components/ui/Skeleton'

/* --------------------------------------------------------------------------
   Prescription Form Skeleton
   -------------------------------------------------------------------------- */

/**
 * Loading skeleton for the prescription form page.
 * Matches the structure of PrescriptionForm.tsx
 */
export function PrescriptionFormSkeleton() {
  return (
    <div className="space-y-6">
      {/* Page header skeleton */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton width="w-48" height="h-8" />
            <Skeleton width="w-32" height="h-4" />
          </div>
          <Skeleton variant="button" width="w-24" />
        </div>
      </div>

      {/* Patient Information Section */}
      <Card>
        <div className="space-y-5">
          {/* Section header */}
          <div className="flex items-center gap-3">
            <Skeleton variant="avatar" width="w-8" height="h-8" />
            <Skeleton width="w-40" height="h-5" />
          </div>

          {/* Patient form fields */}
          <PatientFormSkeleton />
        </div>
      </Card>

      {/* Product Selection Section */}
      <Card>
        <div className="space-y-5">
          {/* Section header */}
          <div className="flex items-center gap-3">
            <Skeleton variant="avatar" width="w-8" height="h-8" />
            <Skeleton width="w-36" height="h-5" />
          </div>

          {/* Product search/select */}
          <SkeletonFormField />
        </div>
      </Card>

      {/* Submit button */}
      <div className="flex justify-end">
        <Skeleton variant="button" width="w-48" height="h-12" />
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Patient Form Skeleton
   -------------------------------------------------------------------------- */

/**
 * Loading skeleton for the patient information form.
 * Matches the structure of PatientForm.tsx
 */
export function PatientFormSkeleton() {
  return (
    <div className="space-y-5">
      {/* Name row: First, Middle, Last */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
        <div className="sm:col-span-2">
          <SkeletonFormField />
        </div>
        <div className="sm:col-span-2">
          <SkeletonFormField />
        </div>
        <div className="sm:col-span-2">
          <SkeletonFormField />
        </div>
      </div>

      {/* DOB and Phone row */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <SkeletonFormField />
        <SkeletonFormField />
      </div>

      {/* Address section */}
      <div className="space-y-5">
        <Skeleton width="w-32" height="h-4" />
        <SkeletonFormField />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
          <div className="sm:col-span-2">
            <SkeletonFormField />
          </div>
          <div className="sm:col-span-2">
            <SkeletonFormField />
          </div>
          <div className="sm:col-span-2">
            <SkeletonFormField />
          </div>
        </div>
      </div>

      {/* Allergies textarea */}
      <div className="space-y-2">
        <Skeleton width="w-20" height="h-3" />
        <Skeleton width="w-full" height="h-20" className="rounded-md" />
        <Skeleton width="w-48" height="h-3" />
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Profile Form Skeleton
   -------------------------------------------------------------------------- */

/**
 * Loading skeleton for the provider profile form.
 * Matches the structure of Profile.tsx
 */
export function ProfileFormSkeleton() {
  return (
    <Card>
      {/* Header */}
      <div className="mb-6 space-y-2 text-center">
        <Skeleton width="w-48 mx-auto" height="h-8" />
        <Skeleton width="w-64 mx-auto" height="h-4" />
      </div>

      {/* Form fields */}
      <div className="space-y-5">
        {/* Full Name */}
        <SkeletonFormField />

        {/* NPI */}
        <div className="space-y-2">
          <Skeleton width="w-48" height="h-3" />
          <Skeleton variant="input" width="w-full" />
          <Skeleton width="w-32" height="h-3" />
        </div>

        {/* License Number and State */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <SkeletonFormField />
          <SkeletonFormField />
        </div>

        {/* Phone and Fax */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <SkeletonFormField />
          <SkeletonFormField />
        </div>
      </div>

      {/* Submit button */}
      <div className="mt-8">
        <Skeleton variant="button" width="w-full" height="h-12" />
      </div>
    </Card>
  )
}

/* --------------------------------------------------------------------------
   Admin Table Skeleton
   -------------------------------------------------------------------------- */

/**
 * Loading skeleton for admin table pages (Catalog, Providers).
 * Provides a consistent skeleton structure for tables.
 */
export function AdminTableSkeleton({
  columns = 5,
  rows = 5,
}: {
  columns?: number
  rows?: number
}) {
  return (
    <div className="space-y-4">
      {/* Search and filter bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Skeleton variant="input" width="w-full" />
        </div>
        <Skeleton variant="button" width="w-32" />
      </div>

      {/* Results count */}
      <Skeleton width="w-48" height="h-4" />

      {/* Table */}
      <SkeletonTable rows={rows} columns={columns} />
    </div>
  )
}

/* --------------------------------------------------------------------------
   Admin Settings Skeleton
   -------------------------------------------------------------------------- */

/**
 * Loading skeleton for the admin settings page.
 * Matches the structure of Settings.tsx
 */
export function AdminSettingsSkeleton() {
  return (
    <div className="space-y-6">
      {/* Page title */}
      <Skeleton width="w-40" height="h-9" />

      {/* Pharmacy Information Card */}
      <Card>
        <Skeleton width="w-48" height="h-6" className="mb-4" />
        <div className="space-y-4">
          {/* Pharmacy name (read-only) */}
          <SkeletonFormField />

          {/* Phone and Fax */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SkeletonFormField />
            <SkeletonFormField />
          </div>

          {/* Street Address */}
          <SkeletonFormField />

          {/* City, State, ZIP */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SkeletonFormField />
            <SkeletonFormField />
            <SkeletonFormField />
          </div>

          {/* NPI and State License */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SkeletonFormField />
            <SkeletonFormField />
          </div>
        </div>
      </Card>

      {/* Branding Card */}
      <Card>
        <Skeleton width="w-24" height="h-6" className="mb-4" />
        <div className="space-y-4">
          {/* Logo URL */}
          <SkeletonFormField />

          {/* Colors */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SkeletonFormField />
            <SkeletonFormField />
          </div>
        </div>
      </Card>

      {/* Portal Link Card */}
      <Card>
        <Skeleton width="w-28" height="h-6" className="mb-4" />
        <div className="space-y-4">
          <Skeleton width="w-full" height="h-4" />
          <div className="flex gap-3">
            <Skeleton width="flex-1" height="h-12" className="rounded-lg" />
            <Skeleton variant="button" width="w-28" />
          </div>
        </div>
      </Card>

      {/* Save button */}
      <div className="flex justify-end">
        <Skeleton variant="button" width="w-32" height="h-12" />
      </div>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Login Form Skeleton
   -------------------------------------------------------------------------- */

/**
 * Loading skeleton for the login page.
 */
export function LoginFormSkeleton() {
  return (
    <Card>
      <div className="space-y-6">
        {/* Logo placeholder */}
        <div className="flex justify-center">
          <Skeleton width="w-40" height="h-16" />
        </div>

        {/* Title */}
        <Skeleton width="w-36 mx-auto" height="h-8" />

        {/* Email field */}
        <SkeletonFormField />

        {/* Password field */}
        <SkeletonFormField />

        {/* Remember me checkbox */}
        <div className="flex items-center gap-2">
          <Skeleton width="w-4" height="h-4" />
          <Skeleton width="w-24" height="h-4" />
        </div>

        {/* Submit button */}
        <Skeleton variant="button" width="w-full" height="h-12" />

        {/* Forgot password link */}
        <Skeleton width="w-32 mx-auto" height="h-4" />
      </div>
    </Card>
  )
}

/* --------------------------------------------------------------------------
   Confirmation Page Skeleton
   -------------------------------------------------------------------------- */

/**
 * Loading skeleton for the prescription confirmation page.
 */
export function ConfirmationSkeleton() {
  return (
    <Card>
      <div className="space-y-6 text-center py-8">
        {/* Success icon placeholder */}
        <div className="flex justify-center">
          <Skeleton variant="avatar" width="w-16" height="h-16" />
        </div>

        {/* Title */}
        <Skeleton width="w-56 mx-auto" height="h-8" />

        {/* Subtitle */}
        <Skeleton width="w-72 mx-auto" height="h-4" />

        {/* Reference number */}
        <div className="py-4">
          <Skeleton width="w-28 mx-auto" height="h-3" className="mb-2" />
          <Skeleton width="w-40 mx-auto" height="h-6" />
        </div>

        {/* Details */}
        <div className="space-y-2">
          <Skeleton width="w-48 mx-auto" height="h-4" />
          <Skeleton width="w-36 mx-auto" height="h-4" />
        </div>

        {/* Button */}
        <div className="pt-4">
          <Skeleton variant="button" width="w-48 mx-auto" height="h-12" />
        </div>
      </div>
    </Card>
  )
}

export default {
  PrescriptionFormSkeleton,
  PatientFormSkeleton,
  ProfileFormSkeleton,
  AdminTableSkeleton,
  AdminSettingsSkeleton,
  LoginFormSkeleton,
  ConfirmationSkeleton,
}
